// components/VideoQuestionPanel.jsx — ask about the video you are watching.
//
// Deliberately NOT the follow-up composer under the player. That one spends a credit and
// generates a WHOLE NEW VIDEO; this one answers about the video already on screen, via
// POST /api/video-chat/{id} with the transcript as context. Two very different costs behind
// two similar-looking text boxes, which is why nothing here ever says "generate".
//
// ── The design rules this follows, and why ────────────────────────────────────────────────
//
// 1. CONCENTRIC RADII. inner radius = outer radius − gap (border + padding). The first cut
//    had a 12px outer radius, 12px padding and 8px inner radius, which by that rule should
//    have been 0 — the mismatch is what read as "corners too sharp": tight outer corners
//    wrapped around rounder inner ones with no room between them. Now 22 − 10 = 12, so every
//    inner corner runs parallel to the shell.
// 2. DARK-MODE ELEVATION IS LIGHTER, NOT DARKER. Raised surfaces in a dark theme get lighter
//    as they rise, paired with a shadow. The panel sat at #0F172A — darker than the app and
//    barely above the black video, so it read as a hole rather than a floating surface. It is
//    now slate-800 over a strong shadow.
// 3. NO BUBBLE FOR THE ASSISTANT. Bubble-on-bubble reads as casual texting and undermines a
//    tool people are trying to learn from; the convention for serious assistants is full
//    width for the answer, with alignment and colour distinguishing the question. It also
//    buys back ~40px of line width, which rendered LaTeX badly needs at this size.
// 4. THE EMPTY STATE IS THE WHOLE GAME. An empty box with a placeholder paralyses people, so
//    it opens on conversation starters — and they disappear once a conversation exists, at
//    which point they are just friction.
// 5. CONTRAST. Body text ≥ 4.5:1, so the muted greys here are /75 and /60 rather than the
//    /40s that made the first version look like a screenful of disabled fields.
//
// Sources are cited in the message that shipped this; the rules above are the durable part.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsPhone } from "../hooks/useIsPhone";
import { X, ArrowUp, ChevronRight } from "lucide-react";
import { apiService } from "../apiService";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import { useT } from "../i18n/useT";
import { renderLatex } from "../study/renderLatex";

const VideoQuestionPanel = ({ videoId, open, onClose }) => {
  const t = useT();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  // On a phone this is a BOTTOM SHEET over the page, not a box over the video — see the
  // container below.
  const isPhone = useIsPhone();

  // Pin the newest turn into view.
  //
  // Sets scrollTop on the panel's OWN scroller rather than calling scrollIntoView on a
  // sentinel: scrollIntoView walks up and scrolls every scrollable ancestor, which on this
  // page means yanking the document down and pushing the video off the top of the window.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  // Click-away and Escape.
  //
  // The ref test is explicit rather than relying on the stopPropagation handlers below to
  // keep inside-clicks away from document. Those exist to protect the video's play/pause
  // layer, and dismissal should not silently depend on a guard that is there for another
  // reason — delete one and the other breaks with no obvious connection.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const send = useCallback(
    async (text) => {
      const message = (text ?? input).trim();
      if (!message || sending) return;

      // Snapshot the history BEFORE the new turn goes in — the API wants the conversation so
      // far as context, not a list already containing the question it is being asked.
      const history = messages;

      setInput("");
      setError(null);
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setSending(true);
      try {
        const res = await apiService.sendVideoChat(videoId, message, history);
        if (res?.success) {
          setMessages((prev) => [...prev, { role: "assistant", content: res.response }]);
        } else {
          setError(GENERIC_ERROR_MESSAGE);
        }
      } catch (err) {
        // A spent daily quota is expected product behaviour, not a fault, and saying so is
        // the difference between "come back tomorrow" and retrying a button that cannot work.
        setError(err?.status === 429 ? t("video_ask_limit_reached") : GENERIC_ERROR_MESSAGE);
      } finally {
        setSending(false);
      }
    },
    [input, sending, messages, videoId, t],
  );

  const chips = [
    t("video_ask_chip_explain"),
    t("video_ask_chip_example"),
    t("video_ask_chip_why"),
  ];
  const empty = messages.length === 0 && !sending;

  const panel = (
    <div
      ref={rootRef}
      /* Stays MOUNTED when closed and fades, so a conversation survives being dismissed and
         reopened. Unmounting would hand back an empty panel every time.

         Height is the CONTENT's, capped 84px short of the frame's bottom (top-3 = 12, control
         bar = 72) so it never covers the controls. A fixed ceiling, not one tracking the bar's
         auto-hide — that resized the panel under the reader every three seconds. */
      className={
        isPhone
          /* PHONE: a bottom sheet over the page, not a panel over the picture.
             Overlaid on the frame it was 340px capped to 70% — 253px of a 361px-wide player,
             and the 84px bottom cap was measured against a DESKTOP control bar. The result
             covered most of the video it was supposed to be about. There is no honest overlay
             size on a 361x203 frame, so on a phone it stops being an overlay: full width,
             anchored to the bottom of the screen, capped at 70vh, with the video still visible
             above it. */
          ? `fixed inset-x-0 bottom-0 z-[70] max-h-[70dvh]
             flex flex-col overflow-hidden p-2.5
             rounded-t-[22px] bg-[#1E293B] border-t border-white/[0.08]
             shadow-[0_-16px_48px_rgba(0,0,0,0.55)]
             transition-transform duration-200 ease-out ${
               open ? "translate-y-0" : "translate-y-full pointer-events-none"
             }`
          : `absolute right-3 top-3 max-h-[calc(100%-84px)] z-30 w-[340px] max-w-[70%]
             flex flex-col overflow-hidden p-2.5
             rounded-[22px] bg-[#1E293B] border border-white/[0.08]
             shadow-[0_16px_48px_rgba(0,0,0,0.55),0_2px_8px_rgba(0,0,0,0.35)]
             transition-all duration-200 ease-out ${
               open
                 ? "opacity-100 translate-y-0 scale-100"
                 : "opacity-0 -translate-y-1 scale-[0.98] pointer-events-none"
             }`
      }
      /* The play/pause hit area covers the whole frame. Without these, clicking into the input
         resumes the video and every space typed toggles playback — the player's key handler is
         bound to window, so React's synthetic bubbling is what has to be cut off. */
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {/* Header. A label, not a title bar competing with the video. */}
      <div className="flex items-center justify-between pl-1.5 pr-0.5 pb-2 flex-shrink-0">
        <p className="text-white/60 text-[11px] font-bold uppercase tracking-[0.09em]">
          {t("video_ask_title")}
        </p>
        <button
          onClick={onClose}
          className="w-7 h-7 -mr-0.5 rounded-lg flex items-center justify-center
            text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors"
          title={t("video_ask_close")}
          aria-label={t("video_ask_close")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Conversation. aria-live so a screen reader announces the answer, which otherwise
          arrives silently — the reader's focus stays in the composer on submit by design. */}
      <div
        ref={scrollRef}
        aria-live="polite"
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2.5"
      >
        {empty && (
          /* Conversation starters, stacked full width. Three pills of differing lengths wrap
             2-then-1 in a 340px box and read as a mistake; rows are also a bigger target.
             The chevron is the affordance — without it these look like disabled fields, which
             is exactly how the first version read. */
          <div className="flex flex-col gap-1.5">
            {chips.map((chip) => (
              <button
                key={chip}
                onClick={() => send(chip)}
                className="group flex items-center justify-between gap-2 text-left
                  pl-3 pr-2.5 py-2.5 rounded-xl text-[13px] text-white/75
                  bg-white/[0.05] border border-white/[0.09]
                  hover:bg-white/[0.11] hover:border-white/20 hover:text-white
                  transition-colors"
              >
                <span>{chip}</span>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 text-white/25 group-hover:text-white/70 transition-colors" />
              </button>
            ))}
          </div>
        )}

        {messages.map((msg, i) =>
          msg.role === "user" ? (
            /* Rendered as TEXT, deliberately. The assistant branch goes through
               dangerouslySetInnerHTML, and sending the user's own keystrokes down that path
               turns a typed <img onerror> into script — which then rides the Firestore
               question log into any admin view that displays it. */
            <div
              key={i}
              className="max-w-[85%] self-end rounded-xl px-3 py-2 text-[13px] leading-relaxed
                whitespace-pre-wrap bg-[#635BFF] text-white"
            >
              {msg.content}
            </div>
          ) : (
            <div
              key={i}
              /* No bubble: full width, distinguished from the question by alignment and colour
                 instead. The model answers in Markdown + $LaTeX$, so this goes through the
                 shared renderer — without it, "$h$" and "###" reached the screen literally.
                 overflow-x-auto keeps a wide $$display$$ block inside the panel rather than
                 stretching it across the video. */
              className="px-1 text-[13px] leading-[1.65] text-white/[0.88] overflow-x-auto
                [&_.katex]:text-[1em] [&_strong]:text-white [&_li]:my-1
                [&_ol]:my-1 [&_ul]:my-1"
              dangerouslySetInnerHTML={{ __html: renderLatex(msg.content) }}
            />
          ),
        )}

        {/* Pending. A pulsing label, never a spinner — and it holds still for anyone who has
            asked the OS to reduce motion. */}
        {sending && (
          <p className="px-1 text-white/55 text-[13px] animate-pulse motion-reduce:animate-none">
            {t("video_ask_thinking")}
          </p>
        )}

        {error && (
          <p className="text-[13px] leading-relaxed text-[#BFDBFE] bg-[#635BFF]/20
            border border-[#635BFF]/30 rounded-xl px-3 py-2">
            {error}
          </p>
        )}
      </div>

      {/* Composer. One field, radius 12 = the shell's 22 minus its 10px padding, so its
          corners stay parallel to the panel's. The row clears 44px so the send target is a
          comfortable size rather than a 28px dot. */}
      <div className="pt-2.5 flex-shrink-0">
        <div
          className="flex items-center gap-1.5 pl-3.5 pr-1.5 py-1.5 rounded-xl
            bg-white/[0.06] border border-white/[0.09]
            focus-within:border-[#635BFF] focus-within:bg-white/[0.09] transition-colors"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") send();
            }}
            placeholder={t("video_ask_placeholder")}
            disabled={sending}
            /* No autoFocus. The usual way to pause is the space bar and the usual way to
               resume is another space bar — which, with the caret parked in here, would type
               a space instead of restarting the video. */
            className="flex-1 min-w-0 bg-transparent text-[13px] text-white
              placeholder-white/45 outline-none disabled:opacity-50"
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || sending}
            className="w-8 h-8 rounded-lg bg-[#635BFF] text-white hover:bg-[#5851e0]
              active:scale-95 disabled:bg-white/[0.08] disabled:text-white/25
              transition-all flex-shrink-0 flex items-center justify-center"
            aria-label={t("video_ask_placeholder")}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // Portaled to <body> on a phone. `position: fixed` resolves against the nearest ancestor
  // with a transform, and the player's container animates — left in place the sheet would be
  // pinned to the bottom of the VIDEO rather than the screen, which is the bug it exists to
  // fix. On desktop it stays inside the player, where being positioned against the frame is
  // exactly what is wanted.
  return isPhone ? createPortal(panel, document.body) : panel;
};

export default VideoQuestionPanel;
